/** @jsxImportSource @emotion/react */
import React, { ReactNode, useEffect } from 'react';
import Header from 'components/Header';
import MainNav from 'components/MainNav';
import { Theme, useTheme } from '@mui/material';
import { UseStyles } from 'types/styles';
import { Footer } from 'components/Footer';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import * as ga from 'application/ga';

interface ICommonPageLayoutProps {
  children?: ReactNode;
}

const CommonPageLayout: React.FC<ICommonPageLayoutProps> = ({ children }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const location = useLocation();

  useEffect(() => {
    ga.pageView();
  }, [location]);

  return (
    <>
      <Helmet titleTemplate={`%s. Health Emergency Preparedness, Response and Resilience (HEPR)`} />
      <div css={classes.CommonPageLayout}>
        <Header sx={classes.header} />
        <aside css={classes.aside}>
          <MainNav className={classes.nav} />
        </aside>
        <div css={classes.main}>
          <main css={classes.pageContent}>{children}</main>
        </div>
        <Footer className={classes.footer} />
      </div>
    </>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  CommonPageLayout: {
    display: 'grid',
    gridTemplateAreas: `
      "h h"
      "a m"
      "f f"
    `,
    minHeight: '100vh',
    gridTemplateRows: 'auto 1fr',
    gridTemplateColumns: '240px 1fr',
  },
  header: {
    gridArea: 'h',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  aside: {
    gridArea: 'a',
    borderRight: `1px solid ${theme.palette.custom.primary.grey[50]}`,
  },
  nav: {
    position: 'sticky',
    top: 110,
  },
  main: {
    gridArea: 'm',
    overflowY: 'auto',
    backgroundColor: theme.palette.custom.primary.grey[20],
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    marginTop: 'auto',
    gridArea: 'f',
  },
});

export default CommonPageLayout;
